import { logger } from '../lib/logger';
import { supabase } from '../integrations/supabase/client';
import { createServiceResponse, createErrorResponse, createServiceError, ServiceResponse, IService } from './service-registry';
import { clientService } from './client.service';
import type { Account, AccountingCategory, AccountingKind, Transaction, RecurringRule, CashflowSummary, SubscriptionTransaction, SubscriptionRenewalAlert, SubscriptionCostSummary } from '@flowstate/types';
interface DbSubscriptionTransactionRow {
    recurring_rules: Array<{
        id: string;
        frequency: string;
        next_occurrence: string;
    }> | null;
    categories_accounting: Array<{
        id: string;
        name: string;
    }> | null;
    [key: string]: unknown;
}
interface DbRenewalTransaction {
    id: string;
    vendor_name: string | null;
    amount: number;
    currency: string;
    description: string | null;
    recurring_rules: Array<{
        next_occurrence: string;
        frequency: string;
    }> | null;
}
/**
 * Accounting Service
 * Manages accounts, categories, transactions, recurring rules, assets, and liabilities.
 * Aligned with Phase 4 architecture.
 */
export class AccountingService implements IService {
    readonly name = 'accounting';
    async initialize(): Promise<void> {
        // no-op
    }
    async dispose(): Promise<void> {
        // no-op
    }
    // ============================================================================
    // ACCOUNTS
    // ============================================================================
    async listAccounts(userId?: string): Promise<ServiceResponse<Account[]>> {
        try {
            const query = supabase.from('accounts').select('*');
            if (userId)
                query.eq('user_id', userId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'ACCOUNTS_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data as Account[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'ACCOUNTS_FETCH_ERROR', service: this.name };
        }
    }
    async listCategories(userId?: string): Promise<ServiceResponse<AccountingCategory[]>> {
        try {
            const query = supabase.from('categories_accounting').select('*');
            if (userId)
                query.eq('user_id', userId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'CATEGORIES_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data as AccountingCategory[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'CATEGORIES_FETCH_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // TRANSACTIONS
    // ============================================================================
    async listTransactions(params: {
        userId?: string;
        from?: string;
        to?: string;
        categoryId?: string;
        accountId?: string;
        includePrivate?: boolean;
    } = {}): Promise<ServiceResponse<Transaction[]>> {
        try {
            const { userId, from, to, categoryId, accountId } = params;
            let query = supabase.from('transactions').select('*');
            if (userId)
                query = query.eq('user_id', userId);
            if (from)
                query = query.gte('date', from);
            if (to)
                query = query.lte('date', to);
            if (categoryId)
                query = query.eq('category_id', categoryId);
            if (accountId)
                query = query.eq('account_id', accountId);
            query = query.order('date', { ascending: false });
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'TRANSACTIONS_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data as Transaction[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TRANSACTIONS_FETCH_ERROR', service: this.name };
        }
    }
    async createTransaction(payload: Omit<Transaction, 'id' | 'created_at'>): Promise<ServiceResponse<Transaction>> {
        try {
            const { data, error } = await supabase
                .from('transactions')
                .insert(payload)
                .select('*')
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'TRANSACTION_CREATE_FAILED', service: this.name };
            }
            const transaction = data as Transaction;
            // 🚀 CRM AUTO-LOG: Log to client activity if linked to a client
            try {
                if ((transaction as unknown as Record<string, unknown>).client_id) {
                    await clientService.createClientActivity({
                        client_id: (transaction as unknown as Record<string, unknown>).client_id as string,
                        type: 'payment',
                        title: `Payment ${transaction.type === 'income' ? 'Received' : 'Recorded'}`,
                        description: `${transaction.description || 'Transaction'}: ${transaction.amount} ${transaction.currency}`,
                        metadata: {
                            transaction_id: transaction.id,
                            transaction_type: transaction.type,
                            amount: transaction.amount,
                            currency: transaction.currency
                        }
                    });
                }
            }
            catch (logError) {
                logger.warn("Failed to auto-log accounting transaction:", { error: logError });
            }
            return { success: true, data: transaction };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TRANSACTION_CREATE_ERROR', service: this.name };
        }
    }
    async updateTransaction(id: string, updates: Partial<Transaction>): Promise<ServiceResponse<Transaction>> {
        try {
            const { data, error } = await supabase
                .from('transactions')
                .update(updates)
                .eq('id', id)
                .select('*')
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'TRANSACTION_UPDATE_FAILED', service: this.name };
            }
            return { success: true, data: data as Transaction };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TRANSACTION_UPDATE_ERROR', service: this.name };
        }
    }
    async deleteTransaction(id: string): Promise<ServiceResponse<void>> {
        try {
            const { error } = await supabase.from('transactions').delete().eq('id', id);
            if (error) {
                return { success: false, error: error.message, code: 'TRANSACTION_DELETE_FAILED', service: this.name };
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'TRANSACTION_DELETE_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // RECURRING RULES
    // ============================================================================
    async listRecurring(userId?: string): Promise<ServiceResponse<RecurringRule[]>> {
        try {
            const query = supabase.from('recurring_rules').select('*');
            if (userId)
                query.eq('user_id', userId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'RECURRING_RULES_FETCH_FAILED', service: this.name };
            }
            return { success: true, data: data as RecurringRule[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RECURRING_RULES_FETCH_ERROR', service: this.name };
        }
    }
    async createRecurring(rule: Omit<RecurringRule, 'id'>): Promise<ServiceResponse<RecurringRule>> {
        try {
            const { data, error } = await supabase
                .from('recurring_rules')
                .insert(rule)
                .select('*')
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'RECURRING_RULE_CREATE_FAILED', service: this.name };
            }
            return { success: true, data: data as RecurringRule };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RECURRING_RULE_CREATE_ERROR', service: this.name };
        }
    }
    async updateRecurring(id: string, updates: Partial<RecurringRule>): Promise<ServiceResponse<RecurringRule>> {
        try {
            const { data, error } = await supabase
                .from('recurring_rules')
                .update(updates)
                .eq('id', id)
                .select('*')
                .single();
            if (error) {
                return { success: false, error: error.message, code: 'RECURRING_RULE_UPDATE_FAILED', service: this.name };
            }
            return { success: true, data: data as RecurringRule };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RECURRING_RULE_UPDATE_ERROR', service: this.name };
        }
    }
    async deleteRecurring(id: string): Promise<ServiceResponse<void>> {
        try {
            const { error } = await supabase.from('recurring_rules').delete().eq('id', id);
            if (error) {
                return { success: false, error: error.message, code: 'RECURRING_RULE_DELETE_FAILED', service: this.name };
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RECURRING_RULE_DELETE_ERROR', service: this.name };
        }
    }
    // ============================================================================
    // SUMMARIES & ANALYTICS
    // ============================================================================
    async getCashflowSummary(userId: string, from: string, to: string): Promise<ServiceResponse<CashflowSummary[]>> {
        try {
            const { data, error } = await supabase
                .from('v_user_cashflow_summary')
                .select('*')
                .eq('user_id', userId)
                .gte('period', from.slice(0, 7))
                .lte('period', to.slice(0, 7));
            if (error) {
                return { success: true, data: [] };
            }
            return { success: true, data: data as unknown as CashflowSummary[] };
        }
        catch (error) {
            return { success: true, data: [] };
        }
    }
    // ============================================================================
    // SUBSCRIPTION MANAGEMENT
    // ============================================================================
    async listSubscriptions(params: {
        userId?: string;
        status?: string;
        includeInactive?: boolean;
    } = {}): Promise<ServiceResponse<SubscriptionTransaction[]>> {
        try {
            const { userId, status, includeInactive } = params;
            let query = supabase.from('transactions')
                .select(`
          *,
          recurring_rules!recurring_rules_template_transaction_id_fkey (
            id,
            frequency,
            next_occurrence
          ),
          categories_accounting!transactions_category_id_fkey (
            id,
            name
          )
        `)
                .eq('is_subscription', true)
                .order('date', { ascending: false });
            if (userId)
                query = query.eq('user_id', userId);
            if (!includeInactive) {
                query = query.or('subscription_status.eq.active,subscription_status.is.null');
            }
            else if (status) {
                query = query.eq('subscription_status', status);
            }
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'SUBSCRIPTIONS_FETCH_FAILED', service: this.name };
            }
            const mappedData = (data as unknown as DbSubscriptionTransactionRow[] || []).map((t) => ({
                ...t,
                recurring_rule_id: t.recurring_rules?.[0]?.id || null,
                next_billing_date: t.recurring_rules?.[0]?.next_occurrence || null,
                billing_cycle: t.recurring_rules?.[0]?.frequency || null,
            })) as unknown as SubscriptionTransaction[];
            return { success: true, data: mappedData };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'SUBSCRIPTIONS_FETCH_ERROR', service: this.name };
        }
    }
    async getUpcomingRenewals(params: {
        userId?: string;
        days?: number;
    } = {}): Promise<ServiceResponse<SubscriptionRenewalAlert[]>> {
        try {
            const { userId, days = 30 } = params;
            let query = supabase.from('v_upcoming_subscription_renewals').select('*');
            if (userId)
                query = query.eq('user_id', userId);
            query = query.lte('days_until_renewal', days);
            const { data, error } = await query;
            if (error) {
                // Fallback to direct query if view doesn't exist
                return this.getUpcomingRenewalsFallback(userId, days);
            }
            return { success: true, data: data as SubscriptionRenewalAlert[] };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RENEWALS_FETCH_ERROR', service: this.name };
        }
    }
    private async getUpcomingRenewalsFallback(userId?: string, days: number = 30): Promise<ServiceResponse<SubscriptionRenewalAlert[]>> {
        try {
            let query = supabase.from('transactions')
                .select(`
          id,
          vendor_name,
          amount,
          currency,
          description,
          recurring_rules!recurring_rules_template_transaction_id_fkey (
            next_occurrence,
            frequency
          )
        `)
                .eq('is_subscription', true)
                .eq('subscription_status', 'active');
            if (userId)
                query = query.eq('user_id', userId);
            const { data, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: 'RENEWALS_FALLBACK_FAILED', service: this.name };
            }
            const alerts = (data as unknown as DbRenewalTransaction[] || [])
                .filter((t) => t.recurring_rules?.[0]?.next_occurrence)
                .map((t) => {
                const rule = t.recurring_rules![0];
                return {
                    id: t.id,
                    vendor_name: t.vendor_name || '',
                    amount: t.amount,
                    currency: t.currency,
                    renewal_date: rule.next_occurrence,
                    days_until_renewal: Math.ceil((new Date(rule.next_occurrence).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                    billing_cycle: rule.frequency,
                    description: t.description,
                };
            })
                .filter((alert: SubscriptionRenewalAlert) => alert.days_until_renewal <= days)
                .sort((a: SubscriptionRenewalAlert, b: SubscriptionRenewalAlert) => a.days_until_renewal - b.days_until_renewal);
            return { success: true, data: alerts };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'RENEWALS_FALLBACK_ERROR', service: this.name };
        }
    }
    async getSubscriptionCostSummary(userId?: string): Promise<ServiceResponse<SubscriptionCostSummary>> {
        try {
            const response = await this.listSubscriptions({ userId, includeInactive: false });
            if (!response.success) {
                return { success: false, error: response.error, code: response.code, service: response.service, context: response.context };
            }
            const subscriptions = response.data;
            const convertToMonthly = (amount: number, cycle: string): number => {
                switch (cycle) {
                    case 'monthly': return amount;
                    case 'quarterly': return amount / 3;
                    case 'yearly': return amount / 12;
                    case 'weekly': return amount * 4.33;
                    case 'daily': return amount * 30;
                    default: return amount;
                }
            };
            const monthlyCost = subscriptions.reduce((sum, sub) => sum + convertToMonthly(sub.amount, sub.billing_cycle || 'monthly'), 0);
            const costByCategory = subscriptions.reduce((acc, sub) => {
                const categoryName = sub.categories_accounting?.[0]?.name || 'Uncategorized';
                const monthly = convertToMonthly(sub.amount, sub.billing_cycle || 'monthly');
                acc[categoryName] = (acc[categoryName] || 0) + monthly;
                return acc;
            }, {} as Record<string, number>);
            const renewalsNext30Days = subscriptions.filter(sub => {
                if (!sub.next_billing_date)
                    return false;
                const daysUntil = Math.ceil((new Date(sub.next_billing_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                return daysUntil <= 30;
            }).length;
            return { success: true, data: {
                    total_subscriptions: subscriptions.length,
                    active_subscriptions: subscriptions.filter(s => s.subscription_status === 'active' || !s.subscription_status).length,
                    monthly_cost: monthlyCost,
                    yearly_cost: monthlyCost * 12,
                    renewals_next_30_days: renewalsNext30Days,
                    cost_by_category: costByCategory,
                } };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'COST_SUMMARY_ERROR', service: this.name };
        }
    }
    async createSubscription(payload: Omit<SubscriptionTransaction, 'id' | 'created_at' | 'type'>): Promise<ServiceResponse<SubscriptionTransaction>> {
        try {
            const transactionPayload = {
                ...payload,
                type: 'expense' as const,
                is_subscription: true,
            } as unknown as Omit<Transaction, 'id' | 'created_at'>;
            const response = await this.createTransaction(transactionPayload);
            if (!response.success) {
                return { success: false, error: response.error, code: response.code, service: response.service, context: response.context };
            }
            const transaction = response.data;
            if (payload.auto_renewal) {
                const recurringPayload = {
                    user_id: payload.user_id,
                    template_transaction_id: transaction.id,
                    frequency: payload.billing_cycle || 'monthly',
                    next_occurrence: payload.next_billing_date || new Date().toISOString().slice(0, 10),
                    is_subscription: true,
                    notification_enabled: true,
                    alert_days_before: payload.renewal_alert_days || 30,
                };
                await this.createRecurring(recurringPayload as unknown as Omit<RecurringRule, 'id'>);
            }
            return { success: true, data: transaction as SubscriptionTransaction };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'SUBSCRIPTION_CREATE_ERROR', service: this.name };
        }
    }
    async updateSubscription(id: string, updates: Partial<SubscriptionTransaction>): Promise<ServiceResponse<SubscriptionTransaction>> {
        try {
            const response = await this.updateTransaction(id, updates);
            if (!response.success) {
                return { success: false, error: response.error, code: response.code, service: response.service, context: response.context };
            }
            const transaction = response.data;
            if (updates.auto_renewal !== undefined || updates.billing_cycle || updates.next_billing_date) {
                const { data: recurringRules } = await supabase
                    .from('recurring_rules')
                    .select('*')
                    .eq('template_transaction_id', id)
                    .eq('is_subscription', true);
                if (recurringRules && recurringRules.length > 0) {
                    const ruleId = recurringRules[0].id;
                    if (updates.auto_renewal === false) {
                        await this.deleteRecurring(ruleId);
                    }
                    else {
                        const ruleUpdates: Record<string, unknown> = {};
                        if (updates.billing_cycle)
                            ruleUpdates.frequency = updates.billing_cycle;
                        if (updates.next_billing_date)
                            ruleUpdates.next_occurrence = updates.next_billing_date;
                        if (updates.renewal_alert_days)
                            ruleUpdates.alert_days_before = updates.renewal_alert_days;
                        if (Object.keys(ruleUpdates).length > 0) {
                            await this.updateRecurring(ruleId, ruleUpdates);
                        }
                    }
                }
                else if (updates.auto_renewal === true) {
                    await this.createRecurring({
                        user_id: transaction.user_id,
                        template_transaction_id: id,
                        frequency: updates.billing_cycle || 'monthly',
                        next_occurrence: updates.next_billing_date || new Date().toISOString().slice(0, 10),
                        is_subscription: true,
                        notification_enabled: true,
                        alert_days_before: updates.renewal_alert_days || 30,
                    } as unknown as Omit<RecurringRule, 'id'>);
                }
            }
            return { success: true, data: transaction as SubscriptionTransaction };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'SUBSCRIPTION_UPDATE_ERROR', service: this.name };
        }
    }
    async cancelSubscription(id: string): Promise<ServiceResponse<void>> {
        try {
            await this.updateTransaction(id, { subscription_status: 'cancelled' } as unknown as Partial<Transaction>);
            const { data: recurringRules } = await supabase
                .from('recurring_rules')
                .select('id')
                .eq('template_transaction_id', id)
                .eq('is_subscription', true);
            if (recurringRules && recurringRules.length > 0) {
                await this.deleteRecurring(recurringRules[0].id);
            }
            return { success: true, data: undefined as unknown as void };
        }
        catch (error) {
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error', code: 'SUBSCRIPTION_CANCEL_ERROR', service: this.name };
        }
    }
}
export const accountingService = new AccountingService();
